<script setup>
import { computed } from 'vue';

const props = defineProps({
  classNum: { type: String, required: true },
  levelData: {
    type: Array,
    required: true,
    validator: (value) => value.every(item => item.level && typeof item.percentage === 'number' && typeof item.count === 'number'),
  },
  maxScale: { type: Number, default: 100 },
  chartHeight: { type: String, default: '480px' }
});

const getBarColor = (index) => { 
  const colors = [
    'var(--result-ai-primary-90)',
    'var(--result-ai-primary-80)',  
    'var(--result-ai-primary-70)',
    'var(--result-ai-primary-60)',
  ];
  return colors[index % colors.length];
};

const getBarHeightPercent = (value) => {
  const numericValue = Number(value);
  if (isNaN(numericValue) || props.maxScale === 0) return 0;
  return (numericValue / props.maxScale) * 100;
};

const getBarHeight = (value) => getBarHeightPercent(value) + '%';

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) return 'N';
  return value;
};

const gridLines = computed(() => {
  const lines = [];
  for (let i = 1; i < 11; i++) lines.push(i * 10);
  return lines;
});
</script>

<template>
  <div class="level-chart-wrapper">
    <div class="level-chart-container" :style="{height: chartHeight}">
      
      <div class="chart-y-axis" :style="{height: chartHeight}">        
        <div class="y-axis-label" v-for="n in 11" :key="n" :style="{ bottom: ((n - 1) * 10) + '%' }">
          {{ Math.round(props.maxScale / 10 * (n - 1)) }}%
        </div>
      </div>
      
      <div class="chart-bars-wrap">
        <div class="chart-grid-lines" :style="{height: chartHeight}">
          <p v-for="pos in gridLines" :key="pos" class="grid-line" :style="{ bottom: pos + '%' }"></p>
        </div>

        <div v-for="(item, index) in levelData" :key="index" class="bar-item-group"> 
            <div class="bar-item" :style="{height: chartHeight}">
                <!-- 수치 라벨은 막대 높이만큼 띄워 올린다. 흐름에 두면 막대가 그만큼 눌려 100%가 끝까지 닿지 않는다 -->
                <div class="bar-top-value" :style="{ bottom: getBarHeight(item.percentage) }">
                    <span class="score-value">{{ getDisplayValue(item.percentage) }}%</span>
                    <span class="count-value">({{ getDisplayValue(item.count) }}명)</span>
                </div>
                <div class="bar" :style="{ height: getBarHeight(item.percentage), backgroundColor: getBarColor(index) }"></div>
            </div>
            <div class="group-label">{{ item.level }}</div>
        </div>
      </div>
    </div>   
  </div>
</template>

<style lang="scss" scoped>
.level-chart-wrapper {
  // 100% 막대 위 수치가 박스를 넘지 않도록 위쪽 여백을 라벨 높이(약 40px)보다 크게 잡는다.
  // 아래쪽도 막대 밑에 수준(4수준 등) 라벨이 그대로 붙어 있어서, 그 라벨 높이(약 32px)보다
  // 여백이 작으면 라벨이 박스 테두리 밖으로 삐져나가 겹쳐 보인다.
  padding: 30px 2rem 38px !important;
  border: 1px solid var(--result-ai-border);
  border-radius: 10px;
  // 옆 명단 표 높이에 맞춰 박스가 늘어나므로 남는 공간을 위아래로 고르게 나눈다
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.level-chart-container { 
  display: flex; 
  gap: 0; 
  width: 100%; 
  position: relative; 
  align-items: flex-start; 
}
.chart-y-axis { 
  position: relative; 
  width: 50px;
  z-index: 2; 
  font-size: 1rem; 
  font-weight: 600;
  display: flex; 
  padding-right: 1rem;
  flex-direction: column-reverse; 
  justify-content: space-between; 
  line-height: 10%; 
  text-align: left; 
}
.chart-bars-wrap {
  display: flex;
  flex: 1;
  // 막대 묶음이 고정 폭이면 칸을 넘겨 점선이 박스 밖으로 나간다 → 남는 폭을 나눠 쓰게 한다
  min-width: 0;
  justify-content: space-around;
  position: relative;
}

.chart-grid-lines { 
  width: 100%; 
  position: absolute; 
  top: 0; left: 0; 
} 
.grid-line { 
  position: absolute; 
  top: 0; left: 0; 
  width: 100%; 
  border-bottom: 1px dashed #e0e0e0; 
}

.bar-item-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  z-index: 4;
}
.bar-item { 
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  justify-content: flex-end; 
  width: 100%; 
  padding: 0 20px; 
  position: relative; 
  height: 100%; 
  border-bottom: 2px solid var(--result-ai-border); 
}
.bar-top-value {
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  padding-bottom: 10px;
  font-weight: 600;
  line-height: 1.2;
  font-size: 1rem;
  white-space: nowrap; // 퍼센트/명수가 절대 두 줄로 안 쪼개지게 고정
}

.bar { 
  width: 36px; 
  border-radius: 4px 4px 0 0; 
  position: relative; 
  min-height: 3px; 
}
.group-label { 
  font-size: 16px; 
  font-weight: 500; 
  padding: 5px 0; 
}
.chart-type { 
  border-radius: 5px; 
  text-align: center; 
  line-height: 28px; 
  font-size: 16px; 
  font-weight: 500; 
  margin-left: 60px; 
}
</style>